// Section 13, 18 and 22 (stream M4a): the Justice panel of the Society tab and the case screen.
// Cases pending (the open justice prompts, with the drafted answer) and resolved (crime,
// offender, victims, response, reoffended, fate); for a pending case the offender's history
// (memory lines), hunger state and family, and every response with why it is locked; the
// justice policy and guard mandate as drafted; safety points with their breakdown; deterrence;
// and last season's unexplained losses. Pure: (game, draft) -> what the panel renders.
import {
  catalog,
  evaluateTarget,
  justiceOptions,
  justicePolicyOptions,
  safetyOverview,
  type BuildingTypeId,
  type CaseId,
  type Config,
  type CrimeKind,
  type Decision,
  type Evaluation,
  type Factor,
  type GameState,
  type GuardMandateOption,
  type JusticeBlock,
  type JusticeCase,
  type JusticePrompt,
  type JusticeResponse,
  type MemoryKind,
  type PersonId,
  type PromptId,
  type ResourceFlow,
} from '../sim/api';
import { stampOfTurn, type SeasonStamp } from './calendar';
import { memo } from './memo';
import { jobLabel } from './people';

export const GUARD_MANDATES: readonly GuardMandateOption[] = [
  'deter',
  'patrol-stores',
  'escort-foragers',
];

/** How many resolved cases the panel lists (most recent first). */
export const RESOLVED_SHOWN = 20;

export interface ResponseOptionVM {
  response: JusticeResponse;
  available: boolean;
  /** Why it cannot be chosen: no jail, jail full, locked, or not offered for this case. */
  block: JusticeBlock | 'not-offered' | null;
}

export interface PersonRef {
  id: PersonId;
  name: string;
  /** Still in the settlement; otherwise dead, departed or exiled. */
  present: boolean;
}

export type Fate = 'here' | 'leaving' | 'custody' | 'died' | 'left' | 'exiled';

export interface FamilyMember extends PersonRef {
  relation: 'partner' | 'parent' | 'child' | 'household';
}

export interface MemoryLine extends SeasonStamp {
  kind: MemoryKind;
  amount: number | null;
  otherName: string | null;
}

export interface OffenderVM extends PersonRef {
  ageYears: number;
  jobLabel: string;
  trust: number;
  happiness: number;
  health: number;
  strength: number;
  /** Last season's food received as a share of need, 0..1. */
  fed: number;
  hungry: boolean;
  /** Consecutive seasons short of food. */
  hungrySeasons: number;
  offences: number;
  /** Crime chance this coming season, with its explain() rows. */
  crimeRisk: Evaluation;
  family: FamilyMember[];
  memory: MemoryLine[];
  priorCases: {
    id: CaseId;
    crime: CrimeKind;
    response: JusticeResponse | null;
    stamp: SeasonStamp;
  }[];
}

export interface CaseVM {
  id: CaseId;
  stamp: SeasonStamp;
  crime: CrimeKind;
  offender: PersonRef;
  fate: Fate;
  victims: PersonRef[];
  status: 'pending' | 'resolved';
  response: JusticeResponse | null;
  reoffended: boolean;
}

export interface PendingCaseVM extends CaseVM {
  promptId: PromptId;
  proposed: JusticeResponse;
  /** The drafted answer, or the proposal if the player has not chosen. */
  chosen: JusticeResponse;
  /** True when the draft overrides the proposal for this case. */
  drafted: boolean;
  options: ResponseOptionVM[];
  /** Null when the offender is no longer in the settlement. */
  details: OffenderVM | null;
}

export interface LossVM {
  crime: CrimeKind;
  amount: number | null;
  victim: PersonRef | null;
  building: BuildingTypeId | null;
}

export interface JusticeVM {
  policy: {
    response: JusticeResponse;
    askEachCase: boolean;
    /** The draft changes the standing policy. */
    pending: boolean;
    options: ResponseOptionVM[];
  };
  mandate: { value: GuardMandateOption; pending: boolean; options: readonly GuardMandateOption[] };
  safety: {
    points: number;
    /** Rows summing to `points`: guards, watch posts, a manned palisade. */
    breakdown: Factor[];
    needed: number;
    /** 0..1, met at 1. */
    need: number;
    guardsOnDuty: number;
    undetectedKilling: boolean;
    raidLoss: boolean;
  };
  /** Deterrence strength (crime is divided by it) and the factor on everyone's crime chance. */
  deterrence: { strength: number; factors: Factor[]; crimeFactor: number };
  pending: PendingCaseVM[];
  resolved: CaseVM[];
  resolvedTotal: number;
  /** Last season's undetected crimes ("unexplained loss"). */
  unexplained: LossVM[];
  /** Food and tools lost to theft last season, detected or not. */
  theft: { food: number; tools: number };
}

function refOf(game: GameState, id: PersonId): PersonRef {
  const p = game.people[id];
  if (p !== undefined) return { id, name: p.name, present: true };
  return { id, name: game.formerPeople[id]?.name ?? id, present: false };
}

function fateOf(game: GameState, id: PersonId): Fate {
  const p = game.people[id];
  if (p !== undefined) {
    if (p.status.inCustody !== null) return 'custody';
    return p.plannedDeparture?.reason === 'exile' ? 'leaving' : 'here';
  }
  const fate = game.formerPeople[id]?.fate;
  if (fate === undefined) return 'left';
  return fate.kind === 'died' ? 'died' : fate.kind === 'exiled' ? 'exiled' : 'left';
}

function caseVM(game: GameState, c: JusticeCase): CaseVM {
  return {
    id: c.id,
    stamp: stampOfTurn(c.turn),
    crime: c.crime,
    offender: refOf(game, c.offenderId),
    fate: fateOf(game, c.offenderId),
    victims: c.victimIds.map((v) => refOf(game, v)),
    status: c.status,
    response: c.response,
    reoffended: c.reoffended,
  };
}

function family(game: GameState, id: PersonId): FamilyMember[] {
  const p = game.people[id];
  if (p === undefined) return [];
  const out: FamilyMember[] = [];
  const seen = new Set<PersonId>([id]);
  const add = (other: PersonId, relation: FamilyMember['relation']): void => {
    if (seen.has(other)) return;
    seen.add(other);
    out.push({ ...refOf(game, other), relation });
  };
  if (p.partnerId !== null) add(p.partnerId, 'partner');
  for (const c of p.childIds) add(c, 'child');
  for (const c of p.parentIds) add(c, 'parent');
  for (const m of game.households[p.householdId]?.memberIds ?? []) add(m, 'household');
  return out;
}

function offenderVM(game: GameState, id: PersonId, cfg: Config | undefined): OffenderVM | null {
  const p = game.people[id];
  if (p === undefined) return null;
  const last = game.history[game.history.length - 1];
  const fed = last?.needs[id]?.food ?? 1;
  const risk = evaluateTarget(game, { kind: 'crimeRisk', personId: id }, cfg);
  const { seasonsPerYear, shortThreshold } = catalog();
  return {
    id,
    name: p.name,
    present: true,
    ageYears: Math.floor(p.ageSeasons / seasonsPerYear),
    jobLabel: jobLabel(p.job),
    trust: p.trust,
    happiness: p.happiness,
    health: p.health,
    strength: p.strength,
    fed,
    hungry: fed < shortThreshold,
    hungrySeasons: p.streaks.hungry,
    offences: p.offences,
    crimeRisk: risk,
    family: family(game, id),
    memory: [...p.memory]
      .sort((a, b) => b.turn - a.turn)
      .map((m) => ({
        ...stampOfTurn(m.turn),
        kind: m.kind,
        amount: m.amount ?? null,
        otherName: m.otherId === undefined ? null : refOf(game, m.otherId).name,
      })),
    priorCases: Object.values(game.cases)
      .filter((c) => c.offenderId === id && c.status === 'resolved')
      .sort((a, b) => b.turn - a.turn || (a.id < b.id ? 1 : -1))
      .map((c) => ({ id: c.id, crime: c.crime, response: c.response, stamp: stampOfTurn(c.turn) })),
  };
}

function caseOptions(game: GameState, prompt: JusticePrompt, cfg?: Config): ResponseOptionVM[] {
  return justiceOptions(game, cfg).map((o) => {
    const offered = prompt.options.includes(o.response);
    return {
      response: o.response,
      available: offered,
      block: offered ? null : (o.block ?? 'not-offered'),
    };
  });
}

function flowOf(flows: Record<string, ResourceFlow> | undefined, r: 'food' | 'tools'): number {
  return flows?.[r]?.theft ?? 0;
}

export const justiceVM = memo(
  (game: GameState, draft: Decision | null, cfg?: Config): JusticeVM => {
    const standing = game.policies.justice;
    const drafted = draft?.policies.justice;
    const policy = drafted ?? standing;
    const mandate = draft?.policies.guardMandate ?? game.policies.guardMandate;
    const safety = safetyOverview(game, cfg);

    const pending: PendingCaseVM[] = [];
    for (const prompt of game.openPrompts) {
      if (prompt.kind !== 'justice') continue;
      const c = game.cases[prompt.caseId];
      if (c === undefined) continue;
      const answer = draft?.eventChoices[prompt.id];
      const chosen = answer?.kind === 'justice' ? answer.response : prompt.proposed;
      pending.push({
        ...caseVM(game, c),
        promptId: prompt.id,
        proposed: prompt.proposed,
        chosen,
        drafted: answer?.kind === 'justice' && answer.response !== prompt.proposed,
        options: caseOptions(game, prompt, cfg),
        details: offenderVM(game, c.offenderId, cfg),
      });
    }

    const resolvedAll = Object.values(game.cases)
      .filter((c) => c.status === 'resolved')
      .sort((a, b) => b.turn - a.turn || (a.id < b.id ? 1 : -1));

    const last = game.history[game.history.length - 1];
    const unexplained: LossVM[] = [];
    for (const e of last?.entries ?? []) {
      if (e.kind !== 'crime' || e.detected) continue;
      unexplained.push({
        crime: e.crime,
        amount: e.amount ?? null,
        victim: e.victimId === undefined ? null : refOf(game, e.victimId),
        building: e.buildingId === undefined ? null : (game.buildings[e.buildingId]?.type ?? null),
      });
    }

    return {
      policy: {
        response: policy.response,
        askEachCase: policy.askEachCase,
        pending: drafted !== undefined,
        options: justicePolicyOptions(game, cfg).map((o) => ({
          response: o.response,
          available: o.block === null,
          block: o.block,
        })),
      },
      mandate: {
        value: mandate,
        pending: draft?.policies.guardMandate !== undefined,
        options: GUARD_MANDATES,
      },
      safety: {
        points: safety.points,
        breakdown: safety.breakdown,
        needed: safety.needed,
        need: safety.need,
        guardsOnDuty: safety.guardsOnDuty,
        undetectedKilling: safety.threats.undetectedKilling,
        raidLoss: safety.threats.raidLoss,
      },
      deterrence: {
        strength: safety.deterrence.value,
        factors: safety.deterrence.factors,
        crimeFactor: safety.crimeFactor,
      },
      pending,
      resolved: resolvedAll.slice(0, RESOLVED_SHOWN).map((c) => caseVM(game, c)),
      resolvedTotal: resolvedAll.length,
      unexplained,
      theft: { food: flowOf(last?.flows, 'food'), tools: flowOf(last?.flows, 'tools') },
    };
  },
);
